import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useAuth } from "./hooks/useAuth";
import { MaintenanceScreen } from "./components/MaintenanceScreen";
import { useLocaleStore, LOCALES, type Country } from "./hooks/useLocale";
import i18n from "./i18n";
import { usePageTracking } from "./hooks/usePageTracking";
import { useSyncWatcher } from "./hooks/useSyncWatcher";
import PortalLink from "./components/PortalLink";
import ImpersonationBanner from "./components/ImpersonationBanner";
import ToastContainer from "./components/Toast";
import AuthCheckLoading from "./components/AuthCheckLoading";
import { isFeatureAvailable } from "./lib/featureAvailability";
import { serverPeriodsEnabled } from "./lib/serverPeriods";
import { preloadActivePageCode } from "./lib/startupCode";
import {
  captureBenchmarkNavigationStart,
  observeBenchmarkRoute,
} from "./lib/benchmarkPerformance";

// Route-level code splitting: Login and feature pages load on demand.
// Keep the most-used import thunks referencable for preloading.
// The authenticated shell is not needed by public entry routes.
const importLayout = () => import("./components/Layout");
const Layout = lazy(importLayout);
const importOverview = () => import("./pages/Overview");
const importAnalyse = () => import("./pages/Analyse");
const importBenchmark = () => import("./pages/Benchmark");
const importRepro = () => import("./pages/Repro");
const importGenetik = () => import("./pages/Genetik");
const importAktionsplan = () => import("./pages/Aktionsplan");
const importDateneingabe = () => import("./pages/Dateneingabe");
const importFarmTargets = () => import("./pages/FarmTargets");

// Only module fetching overlaps session verification. RequireAuth still gates
// mounting, every data query and display. The OFF path retains its old schedule.
if (serverPeriodsEnabled && typeof window !== "undefined") {
  void preloadActivePageCode(window.location.pathname, importLayout, {
    "/": importOverview, "/overview": importOverview,
    "/analyse": importAnalyse, "/benchmark": importBenchmark,
  });
}

const Overview = lazy(importOverview);
const Analyse = lazy(importAnalyse);
const Repro = lazy(importRepro);
const Benchmark = lazy(importBenchmark);
const NeuerBetrieb = lazy(() => import("./pages/NeuerBetrieb"));
const Genetik = lazy(importGenetik);
const Aktionsplan = lazy(importAktionsplan);
const Dateneingabe = lazy(importDateneingabe);
const FarmTargets = lazy(importFarmTargets);
const Login = lazy(() => import("./pages/Login"));
const ChangePassword = lazy(() => import("./pages/ChangePassword"));
const BusinessModelCalculator = lazy(() => import("./pages/BusinessModelCalculator"));
const KpiExport = lazy(() => import("./pages/KpiExport"));
const Admin = lazy(() => import("./pages/Admin"));
const Uebersetzungen = lazy(() => import("./pages/Uebersetzungen"));
const Berater = lazy(() => import("./pages/Berater"));
const Tutorial = lazy(() => import("./pages/Tutorial"));
const FeedbackPage = lazy(() => import("./pages/FeedbackPage"));
const FeedbackInboxPage = lazy(() => import("./pages/FeedbackInboxPage"));
const ChangelogPage = lazy(() => import("./pages/ChangelogPage"));
const DataQuality = lazy(() => import("./pages/DataQuality"));
const AccountPage = lazy(() => import("./pages/AccountPage"));
const FactLayerAdmin = lazy(() => import("./pages/FactLayerAdmin"));
const AppRootAdmin = lazy(() => import("./pages/AppRootAdmin"));

const DemoLead = lazy(() => import("./pages/DemoLead"));
const DemoActivate = lazy(() => import("./pages/DemoActivate"));
const Einladungen = lazy(() => import("./pages/Einladungen"));
const InviteRegister = lazy(() => import("./pages/InviteRegister"));
const GuestAccess = lazy(() => import("./pages/GuestAccess"));
const GuestDashboard = lazy(() => import("./pages/GuestDashboard"));
const GuestVisits = lazy(() => import("./pages/GuestVisits"));

function RouteFallback() {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "4rem 2rem",
        color: "var(--color-text-muted, #6c757d)",
        fontSize: "0.95rem",
        gap: "0.6rem",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 18,
          height: 18,
          border: "2px solid var(--color-border, #dee2e6)",
          borderTopColor: "var(--color-primary, #d82e91)",
          borderRadius: "50%",
          display: "inline-block",
          animation: "route-spin 0.7s linear infinite",
        }}
      />
      <style>{"@keyframes route-spin { to { transform: rotate(360deg); } }"}</style>
      Laden …
    </div>
  );
}

/** Preload the most-used route chunks in the background once the app is idle. */
function usePreloadRoutes() {
  useEffect(() => {
    const preload = () => {
      importOverview();
      importAnalyse();
      importBenchmark();
       if (isFeatureAvailable("repro")) importRepro();
       if (isFeatureAvailable("genetik")) importGenetik();
      importAktionsplan();
      importDateneingabe();
    };
    const win = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    if (serverPeriodsEnabled) {
      // "Idle" during an outstanding primary request is not primary readiness.
      // Do not compete with the first page by importing every other feature.
      const id = window.setTimeout(preload, 2500);
      return () => window.clearTimeout(id);
    }
    if (typeof win.requestIdleCallback === "function") {
      const id = win.requestIdleCallback(preload, { timeout: 4000 });
      return () => win.cancelIdleCallback?.(id);
    }
    const t = window.setTimeout(preload, 2000);
    return () => window.clearTimeout(t);
  }, []);
}

function StartupEffects() {
  usePageTracking();
  useSyncWatcher();
  usePreloadRoutes();
  return null;
}

function AuthenticatedStartupEffects() {
  const { user, isLoading, sessionChecked } = useAuth();

  if (!sessionChecked || isLoading || !user) return null;
  return <StartupEffects />;
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, bootstrap, isLoading, sessionChecked, networkError, clearNetworkError } = useAuth();
  const location = useLocation();
  const hydrateLocale = useLocaleStore((s) => s.hydrateFromApi);
  const setDemoLanguage = useLocaleStore((s) => s.setDemoLanguage);

  useEffect(() => {
    if (!sessionChecked) {
      bootstrap();
    }
  }, [sessionChecked, bootstrap]);

  // Bootstrap owns a bounded read-only retry budget. Once exhausted, leave
  // the recovery screen stable until the user explicitly retries.

  // Demo links carry their own signed locale. Never overwrite it with a
  // regular user's saved preference or write it back as a user preference.
  useEffect(() => {
    if (user) {
      if (user.role === "demo") {
        setDemoLanguage(user.demo_language ?? "de");
      } else {
        hydrateLocale();
      }
    }
  }, [user, hydrateLocale, setDemoLanguage]);

  // Ensure i18next language matches the stored country on startup
  useEffect(() => {
    const savedCountry = (
      localStorage.getItem("alta_demo_country")
      ?? localStorage.getItem("alta_country")
      ?? "DE"
    ) as Country;
    const langForCountry = LOCALES[savedCountry]?.lang ?? "de";
    if (i18n.language !== langForCountry) {
      i18n.changeLanguage(langForCountry);
    }
  }, []);

  // Backend unreachable: show maintenance screen instead of blank or redirect
  if (networkError) {
    return (
      <MaintenanceScreen
        onRetry={() => {
          clearNetworkError();
          bootstrap();
        }}
      />
    );
  }

  if (!sessionChecked || isLoading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          fontSize: "1rem",
          color: "var(--color-text-muted, #6c757d)",
        }}
      >
        Laden …
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }
  if (user.role === "guest" && location.pathname !== "/guest/dashboard") {
    return <Navigate to="/guest/dashboard" replace />;
  }

  return <>{location.pathname === "/change-password" && <PortalLink viewport />}{children}</>;
}

function GuestAccessWithPortal() {
  const { user } = useAuth();
  return <>{user && <PortalLink viewport />}<GuestAccess /></>;
}

function RequirePasswordChange({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();

  if (user?.must_change_password) {
    return <Navigate to="/change-password" replace />;
  }

  return <>{children}</>;
}

function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();

  if (user && user.role !== "admin") {
    return (
      <div
        style={{
          padding: "3rem 2rem",
          textAlign: "center",
          color: "var(--color-text-muted, #6c757d)",
        }}
      >
        <div style={{ fontSize: "2rem", marginBottom: "1rem" }}>🔒</div>
        <h2 style={{ color: "var(--color-danger, #d82e91)" }}>Kein Zugriff</h2>
        <p>Diese Seite ist nur für Administratoren zugänglich.</p>
      </div>
    );
  }

  return <>{children}</>;
}

// Kunden (Landwirte) dürfen nur die fachlichen Betriebsseiten sehen.
function RequireNotKunde({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();

  if (user?.role === "kunde") {
    return <Navigate to="/overview" replace />;
  }

  return <>{children}</>;
}

function RequireNotDemo({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  if (user?.role === "demo") {
    return <Navigate to="/overview" replace />;
  }
  return <>{children}</>;
}

function RequireGuest({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  return user?.role === "guest" ? <>{children}</> : <Navigate to="/overview" replace />;
}

function RequireCalculatorAccess({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const allowed = user && ["user", "berater", "admin", "country_admin"].includes(user.role);
  return allowed ? <>{children}</> : <Navigate to="/overview" replace />;
}

function RequireUserManager({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();

  if (user && user.role !== "admin" && user.role !== "country_admin" && user.role !== "berater") {
    return (
      <div
        style={{
          padding: "3rem 2rem",
          textAlign: "center",
          color: "var(--color-text-muted, #6c757d)",
        }}
      >
        <div style={{ fontSize: "2rem", marginBottom: "1rem" }}>🔒</div>
        <h2 style={{ color: "var(--color-danger, #d82e91)" }}>Kein Zugriff</h2>
        <p>Diese Seite ist nur für Administratoren und Berater zugänglich.</p>
      </div>
    );
  }

  return <>{children}</>;
}

function RequireAdminOrCountryAdmin({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();

  if (user && user.role !== "admin" && user.role !== "country_admin") {
    return (
      <div
        style={{
          padding: "3rem 2rem",
          textAlign: "center",
          color: "var(--color-text-muted, #6c757d)",
        }}
      >
        <div style={{ fontSize: "2rem", marginBottom: "1rem" }}>🔒</div>
        <h2 style={{ color: "var(--color-danger, #d82e91)" }}>Kein Zugriff</h2>
        <p>Diese Seite ist nur für Administratoren zugänglich.</p>
      </div>
    );
  }

  return <>{children}</>;
}

function RequireAdminOrBerater({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();

  if (user && user.role !== "admin" && user.role !== "berater") {
    return (
      <div
        style={{
          padding: "3rem 2rem",
          textAlign: "center",
          color: "var(--color-text-muted, #6c757d)",
        }}
      >
        <div style={{ fontSize: "2rem", marginBottom: "1rem" }}>🔒</div>
        <h2 style={{ color: "var(--color-danger, #d82e91)" }}>Kein Zugriff</h2>
        <p>Diese Seite ist nur für Administratoren und Berater zugänglich.</p>
      </div>
    );
  }

  return <>{children}</>;
}

function RequireGuestVisitManager({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  if (user && user.role !== "admin" && user.role !== "berater" && user.role !== "country_admin") {
    return (
      <div style={{ padding: "3rem 2rem", textAlign: "center", color: "var(--color-text-muted, #6c757d)" }}>
        <div style={{ fontSize: "2rem", marginBottom: "1rem" }}>🔒</div>
        <h2 style={{ color: "var(--color-danger, #d82e91)" }}>Kein Zugriff</h2>
        <p>Diese Seite ist nur für Admins, Country-Admins und Berater zugänglich.</p>
      </div>
    );
  }
  return <>{children}</>;
}

// Einladungsverwaltung (task-1394): Betriebsleiter (kunde-Hauptaccounts,
// keine Mitarbeiter-Konten), Berater und Admins. Mitarbeiter → Übersicht.
function RequireInviter({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();

  if (user && (user.role === "user" || (user.role === "kunde" && user.is_staff))) {
    return <Navigate to="/overview" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Suspense fallback={<RouteFallback />}>
    <AuthenticatedStartupEffects />
    <Routes>
      <Route path="/" element={<Navigate to="/overview" replace />} />

      {/* Primary pages */}
      <Route path="/overview" element={<Overview />} />
      <Route path="/betriebsziele" element={<FarmTargets />} />
      <Route path="/analyse" element={<Analyse />} />
      <Route
        path="/repro"
         element={isFeatureAvailable("repro") ? <RequireNotKunde><RequireNotDemo><Repro /></RequireNotDemo></RequireNotKunde> : <Navigate to="/overview" replace />}
      />
      <Route
        path="/benchmark"
        element={<Benchmark />}
      />
      <Route
        path="/genetik"
         element={isFeatureAvailable("genetik") ? <Genetik /> : <Navigate to="/overview" replace />}
      />
      <Route
        path="/aktionsplan"
        element={<Aktionsplan />}
      />
      <Route
        path="/dateneingabe"
        element={<RequireNotKunde><RequireNotDemo><Dateneingabe /></RequireNotDemo></RequireNotKunde>}
      />
      <Route
        path="/geschaeftsmodell-kalkulator"
         element={isFeatureAvailable("businessModelCalculator") ? <RequireCalculatorAccess><BusinessModelCalculator /></RequireCalculatorAccess> : <Navigate to="/overview" replace />}
      />

      {/* Admin pages */}
      <Route
        path="/kpi-export"
        element={
          <RequireAdmin>
            <KpiExport />
          </RequireAdmin>
        }
      />
      <Route
        path="/neuer-betrieb"
        element={
          <RequireAuth>
            <RequireNotKunde>
              <RequireNotDemo>
                <NeuerBetrieb />
              </RequireNotDemo>
            </RequireNotKunde>
          </RequireAuth>
        }
      />

      {/* Admin-only pages */}
      <Route
        path="/admin"
        element={
          <RequireAdminOrCountryAdmin>
            <Admin />
          </RequireAdminOrCountryAdmin>
        }
      />
      <Route
        path="/admin/faktenschicht"
        element={
          <RequireAdmin>
            <FactLayerAdmin />
          </RequireAdmin>
        }
      />
      <Route
        path="/admin/app-root"
        element={
          <RequireAdmin>
            <AppRootAdmin />
          </RequireAdmin>
        }
      />
      <Route
        path="/admin/berater"
        element={
          <RequireUserManager>
            <Berater />
          </RequireUserManager>
        }
      />
      <Route
        path="/uebersetzungen"
        element={
          <RequireAdminOrBerater>
            <Uebersetzungen />
          </RequireAdminOrBerater>
        }
      />

      <Route
        path="/einladungen"
        element={
          <RequireInviter>
            <RequireNotDemo>
              <Einladungen />
            </RequireNotDemo>
          </RequireInviter>
        }
      />
      <Route path="/gastbesuche" element={<RequireGuestVisitManager><GuestVisits /></RequireGuestVisitManager>} />
      <Route path="/guest/dashboard" element={<RequireGuest><GuestDashboard /></RequireGuest>} />

      <Route path="/tutorial" element={<Tutorial />} />
      <Route
        path="/changelog"
        element={
          <RequireAuth>
            <ChangelogPage />
          </RequireAuth>
        }
      />
      <Route path="/feedback" element={<FeedbackPage />} />
      <Route
        path="/account"
        element={
          <RequireAuth>
            <RequireNotDemo>
              <AccountPage />
            </RequireNotDemo>
          </RequireAuth>
        }
      />
      <Route
        path="/feedback-inbox"
        element={
          <RequireAdmin>
            <FeedbackInboxPage />
          </RequireAdmin>
        }
      />
      <Route
        path="/data-quality"
        element={
          <RequireAdmin>
            <DataQuality />
          </RequireAdmin>
        }
      />

      {/* Demo session pages (accessible in demo mode) */}
      <Route path="/demo/qualify" element={<DemoLead />} />
      <Route path="/demo/thanks" element={<DemoThanks />} />

      {/* Demo-Link-Management (berater/admin only) */}
      <Route
        path="/demo-links"
        element={
          <RequireAdminOrBerater>
            <RequireNotDemo>
              <DemoLinks />
            </RequireNotDemo>
          </RequireAdminOrBerater>
        }
      />

      {/* Lead-Dashboard (berater/admin only) */}
      <Route
        path="/leads"
        element={
          <RequireAdminOrBerater>
            <RequireNotDemo>
              <LeadDashboard />
            </RequireNotDemo>
          </RequireAdminOrBerater>
        }
      />

      <Route
        path="*"
        element={
          <div style={{ padding: "2rem", color: "var(--color-text-muted, #6c757d)" }}>
            Seite nicht gefunden (404)
          </div>
        }
      />
    </Routes>
    </Suspense>
  );
}

/** Observe router paths outside lazy route chunks and capture SPA intent early. */
function BenchmarkNavigationObserver() {
  const location = useLocation();
  // This lightweight sibling runs before Routes renders a lazy page. Creating
  // the visit here makes its ID available to the Benchmark query function.
  observeBenchmarkRoute(location.pathname);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = event.composedPath().find(
        (target): target is HTMLAnchorElement => target instanceof HTMLAnchorElement,
      );
      if (!anchor || anchor.target && anchor.target !== "_self") return;
      try {
        const destination = new URL(anchor.href, window.location.href);
        if (destination.origin === window.location.origin) {
          captureBenchmarkNavigationStart(destination.pathname);
        }
      } catch {
        // Ignore malformed/non-navigation hrefs; the router observer still
        // timestamps programmatic transitions when they commit.
      }
    };
    const onPopState = () => captureBenchmarkNavigationStart(window.location.pathname);
    document.addEventListener("click", onClick, true);
    window.addEventListener("popstate", onPopState);
    return () => {
      document.removeEventListener("click", onClick, true);
      window.removeEventListener("popstate", onPopState);
    };
  }, []);

  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <BenchmarkNavigationObserver />
      <Routes>
        <Route
          path="/login"
          element={
            <Suspense fallback={<AuthCheckLoading />}>
              <Login />
            </Suspense>
          }
        />
        {/* Öffentliche Registrierungsseite für Einladungslinks (task-1394) */}
        <Route
          path="/invite/:token"
          element={
            <Suspense fallback={<RouteFallback />}>
              <InviteRegister />
            </Suspense>
          }
        />
        {/* Public demo activation — no authentication required (task-1393) */}
        <Route path="/demo/:token" element={<DemoActivate />} />
        {/* Public guest visit flow — separate from demos and invitations. */}
        <Route path="/guest/:token" element={<Suspense fallback={<RouteFallback />}><GuestAccessWithPortal /></Suspense>} />
        <Route
          path="/change-password"
          element={
            <Suspense fallback={<RouteFallback />}>
              <RequireAuth>
                <ChangePassword />
              </RequireAuth>
            </Suspense>
          }
        />
        <Route
          path="/*"
          element={
            <RequireAuth>
              <RequirePasswordChange>
                <ImpersonationBanner />
                <Suspense fallback={<AuthCheckLoading />}>
                <Layout>
                  <AppRoutes />
                </Layout>
                </Suspense>
              </RequirePasswordChange>
            </RequireAuth>
          }
        />
      </Routes>
      <ToastContainer />
    </BrowserRouter>
  );
}

const DemoThanks = lazy(() => import("./pages/DemoThanks"));

const LeadDashboard = lazy(() => import("./pages/LeadDashboard"));

const DemoLinks = lazy(() => import("./pages/DemoLinks"));
